import React, { useEffect } from 'react';
import styled from 'styled-components';
import { ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, ChefHat, RotateCcw, WifiOff } from 'lucide-react';

export interface RicetteV2Ingrediente {
  ingrediente_id: number;
  ingrediente_nome: string;
  quantita_per_kg: number;
  unita_ricetta: string;
  quantita_necessaria: number;
  is_variegatura: boolean;
  is_aggiunta?: boolean;
}

export interface RicetteV2Gusto {
  gusto_id: number;
  gusto_nome: string;
  gusto_colore: string;
  quantita_kg: number;
  ingredienti: RicetteV2Ingrediente[];
  ha_ricette?: boolean;
  note_produzione?: string;
}

export interface RicetteV2Data {
  dettagli_per_gusto: RicetteV2Gusto[];
  kg_totali: number;
  num_gusti: number;
  num_gusti_senza_ricette?: number;
  offline?: boolean;
  message?: string;
}

export interface RicetteV2Progress {
  signature: string;
  currentIndex: number;
  checkedKeys: string[];
  checkedByGusto?: Record<string, string[]>;
}

interface ProduzioneRicetteFlowV2Props {
  data: RicetteV2Data;
  progress: RicetteV2Progress;
  onProgressChange: (progress: RicetteV2Progress) => void;
  onBack: () => void;
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const HeaderCard = styled.div`
  background: linear-gradient(135deg, #18212f 0%, #2d3436 100%);
  color: white;
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 24px 60px rgba(24, 33, 47, 0.22);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border-radius: 999px;
  padding: 0.7rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.82;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 900;
  line-height: 1.05;
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 680px;
  font-size: 1rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.84);
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.9rem;
  margin-top: 1.25rem;
`;

const SummaryCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  padding: 1rem;
`;

const SummaryLabel = styled.div`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.35rem;
`;

const SummaryValue = styled.div`
  font-size: 1.35rem;
  font-weight: 900;
`;

const AutoSaveBanner = styled.div`
  border-radius: 14px;
  background: #eef9f0;
  border: 1px solid #ccead1;
  color: #1f6f37;
  padding: 1rem 1.15rem;
  font-size: 0.95rem;
  font-weight: 600;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 1.5rem;
  align-items: flex-start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 18px;
  padding: 1rem;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SidebarTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`;

const SidebarHeading = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  color: #18212f;
`;

const SidebarCounter = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: #5f6f7b;
`;

const TasteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  max-height: 70vh;
  overflow: auto;

  @media (max-width: 1080px) {
    max-height: none;
  }
`;

const TasteButton = styled.button<{ $active: boolean; $complete: boolean; $warning: boolean }>`
  border: 2px solid ${({ $active, $warning }) => ($active ? '#18212f' : $warning ? '#f4c7b8' : '#dce3ea')};
  background: ${({ $active, $warning }) => ($active ? '#18212f' : $warning ? '#fff6f2' : '#ffffff')};
  color: ${({ $active }) => ($active ? 'white' : '#18212f')};
  border-radius: 14px;
  padding: 0.85rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  text-align: left;
  box-shadow: ${({ $active }) => ($active ? '0 14px 28px rgba(24, 33, 47, 0.22)' : 'none')};
`;

const TasteIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
  flex: 1;
`;

const TasteSwatch = styled.span<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  border: 2px solid rgba(0, 0, 0, 0.18);
  flex-shrink: 0;
`;

const TasteText = styled.div`
  min-width: 0;
`;

const TasteName = styled.div`
  font-size: 0.98rem;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TasteMeta = styled.div`
  font-size: 0.82rem;
  opacity: 0.75;
  margin-top: 0.2rem;
`;

const TasteStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 800;
  flex-shrink: 0;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StageCard = styled.div`
  background: white;
  border-radius: 18px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.08);
  padding: 1.25rem;
`;

const MainNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  border: 2px solid #18212f;
  background: #18212f;
  color: white;
  border-radius: 999px;
  padding: 0.72rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    border-color: #d5dce3;
    background: #eef2f5;
    color: #98a4af;
    cursor: default;
  }
`;

const SecondaryButton = styled.button`
  border: 2px solid #d5dce3;
  background: white;
  color: #18212f;
  border-radius: 999px;
  padding: 0.72rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
`;

const ProgressBadge = styled.div`
  background: #eef2f5;
  border-radius: 999px;
  color: #40505e;
  padding: 0.65rem 0.95rem;
  font-size: 0.9rem;
  font-weight: 800;
`;

const GustoCard = styled.div<{ $warning: boolean }>`
  border: 2px solid ${({ $warning }) => ($warning ? '#f4c7b8' : '#dce3ea')};
  background: ${({ $warning }) => ($warning ? '#fff8f5' : '#fcfdfe')};
  border-radius: 18px;
  padding: 1.4rem;
`;

const GustoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const GustoIdentity = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  min-width: 0;
`;

const GustoSwatch = styled.div<{ $color: string }>`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  border: 3px solid rgba(24, 33, 47, 0.2);
  flex-shrink: 0;
`;

const GustoHeading = styled.div`
  min-width: 0;
`;

const GustoName = styled.h3`
  margin: 0;
  font-size: 2rem;
  font-weight: 900;
  line-height: 1.05;
  color: #111821;
`;

const GustoShortCode = styled.div`
  margin-top: 0.4rem;
  font-size: 0.82rem;
  color: #5f6f7b;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const GustoKg = styled.div<{ $warning: boolean }>`
  border-radius: 16px;
  background: ${({ $warning }) => ($warning ? '#d16d47' : '#18212f')};
  color: white;
  padding: 0.85rem 1rem;
  font-size: 1.65rem;
  font-weight: 900;
  white-space: nowrap;
`;

const Hint = styled.div`
  margin-bottom: 0.85rem;
  color: #5f6f7b;
  font-size: 0.92rem;
  font-style: italic;
`;

const Notice = styled.div<{ $warning?: boolean }>`
  border-radius: 14px;
  padding: 1rem 1.1rem;
  background: ${({ $warning }) => ($warning ? '#fff3ed' : '#f6f8fa')};
  border: 1px solid ${({ $warning }) => ($warning ? '#f4c7b8' : '#dde5ec')};
  color: ${({ $warning }) => ($warning ? '#9a4b2e' : '#445360')};
  font-size: 0.95rem;
  line-height: 1.5;
`;

const NoteBox = styled.div`
  margin-bottom: 1rem;
  border-radius: 14px;
  background: #fff7db;
  border: 1px solid #f0d58a;
  color: #6e5313;
  padding: 0.95rem 1rem;
  font-size: 0.95rem;
  line-height: 1.55;
`;

const SectionLabel = styled.div`
  margin: 1rem 0 0.6rem;
  font-size: 0.9rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #33424f;
`;

const IngredientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const IngredientButton = styled.button<{ $checked: boolean }>`
  width: 100%;
  border: 2px solid ${({ $checked }) => ($checked ? '#bfe7ca' : '#dce3ea')};
  background: ${({ $checked }) => ($checked ? '#effaf2' : '#ffffff')};
  border-radius: 16px;
  padding: 0.9rem 1rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.85rem;
  align-items: center;
  cursor: pointer;
  text-align: left;
  opacity: ${({ $checked }) => ($checked ? 0.74 : 1)};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const IngredientCheck = styled.div<{ $checked: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border: 3px solid ${({ $checked }) => ($checked ? '#2f9d52' : '#aeb9c4')};
  background: ${({ $checked }) => ($checked ? '#2f9d52' : 'transparent')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 900;
  flex-shrink: 0;
`;

const IngredientName = styled.div<{ $checked: boolean }>`
  font-size: 1.08rem;
  font-weight: 800;
  color: ${({ $checked }) => ($checked ? '#78848f' : '#18212f')};
  text-decoration: ${({ $checked }) => ($checked ? 'line-through' : 'none')};
`;

const IngredientCalc = styled.div<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  color: ${({ $checked }) => ($checked ? '#84909c' : '#445360')};
`;

const IngredientFormula = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
`;

const IngredientAmount = styled.span<{ $checked: boolean }>`
  min-width: 96px;
  text-align: right;
  border-radius: 999px;
  padding: 0.38rem 0.8rem;
  background: ${({ $checked }) => ($checked ? '#dcefe1' : '#eef2f5')};
  color: ${({ $checked }) => ($checked ? '#558465' : '#18212f')};
  font-size: 1.1rem;
  font-weight: 900;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const FooterHint = styled.div`
  color: #5f6f7b;
  font-size: 0.92rem;
  font-weight: 600;
`;

interface PreparedIngrediente extends RicetteV2Ingrediente {
  progressKey: string;
  rowKey: string;
}

interface PreparedGusto extends RicetteV2Gusto {
  preparedIngredienti: PreparedIngrediente[];
}

function getIngredientScopedKey(ingrediente: RicetteV2Ingrediente, index: number) {
  const section = ingrediente.is_variegatura ? 'variegatura' : (ingrediente.is_aggiunta ? 'aggiunta' : 'miscela');
  return `${section}:${ingrediente.ingrediente_id}:${index}`;
}

function normalizeCheckedByGusto(progress: RicetteV2Progress): Record<string, string[]> {
  if (progress.checkedByGusto && typeof progress.checkedByGusto === 'object') {
    return Object.fromEntries(
      Object.entries(progress.checkedByGusto)
        .map(([gustoId, keys]) => [gustoId, Array.from(new Set((keys || []).filter(Boolean)))])
        .filter(([, keys]) => keys.length > 0)
    ) as Record<string, string[]>;
  }

  return progress.checkedKeys.reduce<Record<string, string[]>>((accumulator, rawKey) => {
    const separatorIndex = rawKey.indexOf(':');
    if (separatorIndex <= 0) return accumulator;

    const gustoId = rawKey.slice(0, separatorIndex);
    const scopedKey = rawKey.slice(separatorIndex + 1);
    if (!scopedKey) return accumulator;

    const currentKeys = accumulator[gustoId] || [];
    if (!currentKeys.includes(scopedKey)) {
      accumulator[gustoId] = [...currentKeys, scopedKey];
    }

    return accumulator;
  }, {});
}

function flattenCheckedByGusto(checkedByGusto: Record<string, string[]>) {
  return Object.entries(checkedByGusto).flatMap(([gustoId, keys]) =>
    keys.map((scopedKey) => `${gustoId}:${scopedKey}`)
  );
}

function prepareGusto(gusto: RicetteV2Gusto): PreparedGusto {
  return {
    ...gusto,
    preparedIngredienti: (gusto.ingredienti || []).map((ingrediente, index) => {
      const progressKey = getIngredientScopedKey(ingrediente, index);

      return {
        ...ingrediente,
        progressKey,
        rowKey: `${gusto.gusto_id}:${progressKey}`
      };
    })
  };
}

function getShortTasteLabel(nome: string) {
  const cleaned = nome.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '???';

  const words = cleaned.split(' ').filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
  }

  return cleaned.slice(0, 3).toUpperCase();
}

function clampIndex(index: number, total: number) {
  if (total <= 0) return 0;
  return Math.min(Math.max(index, 0), total - 1);
}

function truncateLabel(value: string, maxLength = 22) {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}…`;
}

function hasRecipe(gusto: RicetteV2Gusto | PreparedGusto) {
  if (gusto.ha_ricette === false) return false;

  if ('preparedIngredienti' in gusto) {
    return gusto.preparedIngredienti.length > 0;
  }

  return Array.isArray(gusto.ingredienti) && gusto.ingredienti.length > 0;
}

function isGustoComplete(gusto: PreparedGusto, checkedByGusto: Record<string, string[]>) {
  if (!hasRecipe(gusto)) return false;

  const checkedKeys = new Set(checkedByGusto[String(gusto.gusto_id)] || []);
  return gusto.preparedIngredienti.every((ingrediente) => checkedKeys.has(ingrediente.progressKey));
}

const ProduzioneRicetteFlowV2: React.FC<ProduzioneRicetteFlowV2Props> = ({
  data,
  progress,
  onProgressChange,
  onBack
}) => {
  const gusti = (data.dettagli_per_gusto || []).map(prepareGusto);
  const checkedByGusto = normalizeCheckedByGusto(progress);
  const currentIndex = clampIndex(progress.currentIndex, gusti.length);
  const gusto = gusti[currentIndex];
  const checkedKeys = new Set(gusto ? checkedByGusto[String(gusto.gusto_id)] || [] : []);

  useEffect(() => {
    if (currentIndex !== progress.currentIndex) {
      onProgressChange({
        ...progress,
        currentIndex
      });
    }
  }, [currentIndex, onProgressChange, progress]);

  if (!gusto) {
    return (
      <Shell>
        <HeaderCard>
          <HeaderTop>
            <BackButton type="button" onClick={onBack}>
              <ArrowLeft size={18} />
              Torna alla produzione
            </BackButton>
          </HeaderTop>
        </HeaderCard>

        <StageCard>
          <Notice $warning>
            Nessuna ricetta disponibile per i gusti selezionati.
          </Notice>
        </StageCard>
      </Shell>
    );
  }

  const updateProgress = (updates: Partial<RicetteV2Progress>) => {
    onProgressChange({
      ...progress,
      ...updates
    });
  };

  const updateCheckedKeysForCurrentGusto = (nextScopedKeys: string[]) => {
    const gustoId = String(gusto.gusto_id);
    const nextCheckedByGusto: Record<string, string[]> = { ...checkedByGusto };
    const sanitizedKeys = Array.from(new Set(nextScopedKeys.filter(Boolean)));

    if (sanitizedKeys.length > 0) {
      nextCheckedByGusto[gustoId] = sanitizedKeys;
    } else {
      delete nextCheckedByGusto[gustoId];
    }

    updateProgress({
      checkedByGusto: nextCheckedByGusto,
      checkedKeys: flattenCheckedByGusto(nextCheckedByGusto)
    });
  };

  const goToIndex = (nextIndex: number) => {
    updateProgress({
      currentIndex: clampIndex(nextIndex, gusti.length)
    });
  };

  const clearCurrentGustoChecks = () => {
    updateCheckedKeysForCurrentGusto([]);
  };

  const ingredientiMiscela = gusto.preparedIngredienti.filter((ingrediente) => !ingrediente.is_variegatura && !ingrediente.is_aggiunta);
  const ingredientiVariegatura = gusto.preparedIngredienti.filter((ingrediente) => ingrediente.is_variegatura);
  const ingredientiAggiunta = gusto.preparedIngredienti.filter((ingrediente) => ingrediente.is_aggiunta);
  const haSezioni = ingredientiVariegatura.length > 0 || ingredientiAggiunta.length > 0;
  const gustoCompletato = isGustoComplete(gusto, checkedByGusto);
  const gustiCompletati = gusti.filter((item) => isGustoComplete(item, checkedByGusto)).length;

  const renderIngredientGroup = (ingredienti: PreparedIngrediente[]) => (
    <IngredientList>
      {ingredienti.map((ingrediente) => {
        const checked = checkedKeys.has(ingrediente.progressKey);

        return (
          <IngredientButton
            key={ingrediente.rowKey}
            type="button"
            $checked={checked}
            onClick={() => {
              const nextCheckedKeys = new Set(checkedByGusto[String(gusto.gusto_id)] || []);

              if (nextCheckedKeys.has(ingrediente.progressKey)) {
                nextCheckedKeys.delete(ingrediente.progressKey);
              } else {
                nextCheckedKeys.add(ingrediente.progressKey);
              }

              updateCheckedKeysForCurrentGusto(Array.from(nextCheckedKeys));
            }}
          >
            <IngredientCheck $checked={checked}>
              {checked ? '✓' : ''}
            </IngredientCheck>
            <IngredientName $checked={checked}>
              {ingrediente.ingrediente_nome}
            </IngredientName>
            <IngredientCalc $checked={checked}>
              <IngredientFormula>
                {ingrediente.quantita_per_kg} {ingrediente.unita_ricetta}/kg x {gusto.quantita_kg} kg
              </IngredientFormula>
              <IngredientAmount $checked={checked}>
                {ingrediente.quantita_necessaria} {ingrediente.unita_ricetta}
              </IngredientAmount>
            </IngredientCalc>
          </IngredientButton>
        );
      })}
    </IngredientList>
  );

  return (
    <Shell>
      <HeaderCard>
        <HeaderTop>
          <div>
            <BackButton type="button" onClick={onBack}>
              <ArrowLeft size={18} />
              Torna alla produzione
            </BackButton>
          </div>

          <HeaderTitle>
            <Eyebrow>
              <ChefHat size={15} />
              Ricette V2
              {data.offline && (
                <>
                  <WifiOff size={15} />
                  Offline
                </>
              )}
            </Eyebrow>
            <Title>Sequenza operativa ricette</Title>
            <Subtitle>
              Ogni gusto ha un riferimento nominale, l&apos;avanzamento resta salvato su questo dispositivo e puoi rientrare nel flusso senza perdere le spunte.
            </Subtitle>
          </HeaderTitle>
        </HeaderTop>

        <SummaryGrid>
          <SummaryCard>
            <SummaryLabel>Gusti</SummaryLabel>
            <SummaryValue>{data.num_gusti}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Kg Totali</SummaryLabel>
            <SummaryValue>{data.kg_totali} kg</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Completati</SummaryLabel>
            <SummaryValue>{gustiCompletati} / {gusti.length}</SummaryValue>
          </SummaryCard>
          <SummaryCard>
            <SummaryLabel>Ricette Mancanti</SummaryLabel>
            <SummaryValue>{data.num_gusti_senza_ricette || 0}</SummaryValue>
          </SummaryCard>
        </SummaryGrid>
      </HeaderCard>

      <AutoSaveBanner>
        Avanzamento salvato automaticamente. Se chiudi questa schermata o cambi pagina, alla riapertura riparti dallo stesso gusto con le stesse spunte.
      </AutoSaveBanner>

      <Layout>
        <Sidebar>
          <SidebarTitle>
            <SidebarHeading>Ordine ricette</SidebarHeading>
            <SidebarCounter>{currentIndex + 1} / {gusti.length}</SidebarCounter>
          </SidebarTitle>

          <TasteList>
            {gusti.map((item, index) => {
              const completed = isGustoComplete(item, checkedByGusto);
              const warning = !hasRecipe(item);

              return (
                <TasteButton
                  key={item.gusto_id}
                  type="button"
                  $active={index === currentIndex}
                  $complete={completed}
                  $warning={warning}
                  onClick={() => goToIndex(index)}
                >
                  <TasteIdentity>
                    <TasteSwatch $color={item.gusto_colore || '#d0d7de'} />
                    <TasteText>
                      <TasteName>{truncateLabel(item.gusto_nome)}</TasteName>
                      <TasteMeta>{item.quantita_kg} kg • {getShortTasteLabel(item.gusto_nome)}</TasteMeta>
                    </TasteText>
                  </TasteIdentity>

                  <TasteStatus>
                    {completed ? (
                      <>
                        <CheckCircle2 size={16} />
                        Fatto
                      </>
                    ) : warning ? (
                      'No ricetta'
                    ) : (
                      `${index + 1}`
                    )}
                  </TasteStatus>
                </TasteButton>
              );
            })}
          </TasteList>
        </Sidebar>

        <Main>
          <StageCard>
            <MainNav>
              <NavGroup>
                <NavButton type="button" onClick={() => goToIndex(currentIndex - 1)} disabled={currentIndex === 0}>
                  <ChevronLeft size={18} />
                  Precedente
                </NavButton>
                <NavButton type="button" onClick={() => goToIndex(currentIndex + 1)} disabled={currentIndex === gusti.length - 1}>
                  Successivo
                  <ChevronRight size={18} />
                </NavButton>
                <SecondaryButton type="button" onClick={clearCurrentGustoChecks}>
                  <RotateCcw size={16} />
                  Azzera spunte gusto
                </SecondaryButton>
              </NavGroup>

              <ProgressBadge>
                Gusto {currentIndex + 1} di {gusti.length}
              </ProgressBadge>
            </MainNav>

            <GustoCard key={gusto.gusto_id} $warning={!hasRecipe(gusto)}>
              <GustoHeader>
                <GustoIdentity>
                  <GustoSwatch $color={gusto.gusto_colore || '#d0d7de'} />
                  <GustoHeading>
                    <GustoName>{gusto.gusto_nome}</GustoName>
                    <GustoShortCode>
                      riferimento {getShortTasteLabel(gusto.gusto_nome)} {gustoCompletato ? '• completato' : ''}
                    </GustoShortCode>
                  </GustoHeading>
                </GustoIdentity>

                <GustoKg $warning={!hasRecipe(gusto)}>
                  {gusto.quantita_kg} kg
                </GustoKg>
              </GustoHeader>

              {gusto.note_produzione && (
                <NoteBox>
                  <strong>Note di produzione:</strong> {gusto.note_produzione}
                </NoteBox>
              )}

              {hasRecipe(gusto) ? (
                <>
                  <Hint>Tocca un ingrediente per segnare che è già stato aggiunto.</Hint>

                  {haSezioni && <SectionLabel>🍦 Miscela / Gelato</SectionLabel>}
                  {renderIngredientGroup(ingredientiMiscela)}

                  {ingredientiVariegatura.length > 0 && (
                    <>
                      <SectionLabel>🌀 Variegatura</SectionLabel>
                      {renderIngredientGroup(ingredientiVariegatura)}
                    </>
                  )}

                  {ingredientiAggiunta.length > 0 && (
                    <>
                      <SectionLabel>🍫 Aggiunta</SectionLabel>
                      {renderIngredientGroup(ingredientiAggiunta)}
                    </>
                  )}
                </>
              ) : (
                <Notice $warning>
                  Nessuna ricetta configurata per questo gusto. Il riferimento nominale resta comunque visibile per aiutare l&apos;operatore a capire dove si trova nella sequenza.
                </Notice>
              )}
            </GustoCard>

            <FooterActions>
              <FooterHint>
                Le spunte di questo gusto restano salvate finché la produzione non viene inviata o azzerata.
              </FooterHint>

              <NavGroup>
                <SecondaryButton type="button" onClick={onBack}>
                  Torna al form
                </SecondaryButton>
                <NavButton type="button" onClick={() => goToIndex(currentIndex + 1)} disabled={currentIndex === gusti.length - 1}>
                  Vai al prossimo gusto
                  <ChevronRight size={18} />
                </NavButton>
              </NavGroup>
            </FooterActions>
          </StageCard>

          {data.offline && (
            <Notice>
              <strong>Modalità offline.</strong> {data.message || 'Le quantità ingredienti sono disponibili, ma alcuni dati accessori potrebbero essere limitati.'}
            </Notice>
          )}
        </Main>
      </Layout>
    </Shell>
  );
};

export default ProduzioneRicetteFlowV2;
